import { useCallback, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { push } from 'connected-react-router'
import { common, layouts } from '@ocean/ui'
import { SessionUtils, ToastUtils } from '@ocean/utils'
import { EnvironmentUtils, UserUtils } from 'utils'
import { handleUnauthorizedEvent } from 'utils/EventUtils'
import { useLazySelector } from 'hooks'
import { clearSessionInfo } from 'core/session/slices/session'
import { redirectToSta } from 'core/staIntegration/slices/staIntegration'
import Skeleton, { SkeletonVariant } from 'components/common/Skeleton'
import simulatorTrainingInstructorILSimulatorListRoutes from 'modules/simulatorTrainings/instructor/instructorLed/SimulatorList/routing/routes'

const { PageLayout, PageSegment } = layouts
const { Decorator } = common

const AUTO_LOGOUT_TIMEOUT_MS = 20 * 60 * 1000 // 20 minutes of inactivity

const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  'mousemove',
  'keydown',
  'mousedown',
  'touchstart',
]

const DigitalDelivery: React.FC = () => {
  const digitalDeliveryRef = useRef<HTMLIFrameElement>(null)
  const logoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const { isLoading, error, customerId, companyName, customerAlias, installationId } =
    useLazySelector(({ system }) => {
      const { isLoading, result, error } = system.customerInformation

      return {
        isLoading,
        customerId: result?.information.customerId,
        installationId: result?.information.currentInstallation.id,
        error,
        companyName: result?.information.companyName,
        customerAlias: result?.information.customerAlias,
      }
    })

  const dispatch = useDispatch()

  const { simulatorDashboardForAdministratorsAvailable } =
    EnvironmentUtils.getPermissions('simulatorTraining')

  // Auto-logout after 20 minutes of inactivity
  useEffect(() => {
    const resetTimer = () => {
      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current)
      logoutTimerRef.current = setTimeout(() => {
        dispatch(clearSessionInfo({ needReturnUrl: true }))
      }, AUTO_LOGOUT_TIMEOUT_MS)
    }

    resetTimer()

    ACTIVITY_EVENTS.forEach(event => window.addEventListener(event, resetTimer))

    return () => {
      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current)
      ACTIVITY_EVENTS.forEach(event => window.removeEventListener(event, resetTimer))
    }
  }, [dispatch])

  const sendTokenAndUserInfo = useCallback(() => {
    if (digitalDeliveryRef.current) {
      const sessionToken = SessionUtils.getSessionToken()
      const { connectedPerson, login } = UserUtils.getUserAccountInfo()

      let olpSessionId = sessionStorage.getItem('DIGITAL_DELIVERY_SESSION_ID')
      if (!olpSessionId) {
        olpSessionId = crypto.randomUUID()
        sessionStorage.setItem('DIGITAL_DELIVERY_SESSION_ID', olpSessionId)
      }

      digitalDeliveryRef.current.contentWindow?.postMessage(
        {
          type: 'OLP_CONTENT_MANAGER',
          sessionToken,
          olpSessionId,
          customerAlias,
          connectedPerson,
          companyName,
          username: login,
          customerId,
          installationId,
          permissions: EnvironmentUtils.getPermissions('digitalDeliveryConfiguration'),
        },
        window.cc.digitalDelivery ?? ''
      )

      //? this is to hide track bars from the window
      const trackVerticalElements = document.querySelectorAll<HTMLElement>(
        '[data-selector="TrackVertical"]'
      )

      if (trackVerticalElements.length >= 2) {
        for (let i = trackVerticalElements.length - 1; i >= trackVerticalElements.length - 2; i--) {
          trackVerticalElements[i].style.visibility = 'hidden'
        }
      }
      const parentDiv = digitalDeliveryRef.current.parentElement
      if (parentDiv) {
        parentDiv.style.overflow = 'hidden'
      }
    }
  }, [companyName, customerAlias, customerId, installationId])

  useEffect(() => {
    const exitDigitalDelivery = (e: MessageEvent) => {
      if (e.data.type === 'EXIT_CONTENT_MANAGER') {
        if (simulatorDashboardForAdministratorsAvailable) {
          dispatch(push(simulatorTrainingInstructorILSimulatorListRoutes.root))
        } else {
          dispatch(redirectToSta())
        }
      }
    }
    // Receives signal from content manager that it is ready to receive token
    const handleDigitalDeliveryReady = (e: MessageEvent) => {
      if (e.data.type === 'CONTENT_MANAGER_READY') {
        sendTokenAndUserInfo()
      }
    }

    window.addEventListener('message', exitDigitalDelivery)
    window.addEventListener('message', handleDigitalDeliveryReady)
    const removeUnauthorizedListener = handleUnauthorizedEvent(dispatch)

    return () => {
      window.removeEventListener('message', exitDigitalDelivery)
      window.removeEventListener('message', handleDigitalDeliveryReady)
      removeUnauthorizedListener()
    }
  }, [dispatch, sendTokenAndUserInfo, simulatorDashboardForAdministratorsAvailable])

  if (isLoading) {
    return <Skeleton variant={SkeletonVariant.Details} />
  }

  if (error) {
    ToastUtils.error(error.error)
    if (simulatorDashboardForAdministratorsAvailable) {
      dispatch(push(simulatorTrainingInstructorILSimulatorListRoutes.root))
    } else {
      dispatch(redirectToSta())
    }
    return null
  }

  return (
    <PageLayout>
      <PageSegment segmentId='content'>
        <Decorator root backgroundColor='white' textColor='black'>
          <iframe
            title='Digital Delivery'
            ref={digitalDeliveryRef}
            src={`${window.cc.digitalDelivery}`}
            onLoad={sendTokenAndUserInfo}
            width='100%'
            height='99%'
            style={{ overflow: 'hidden' }}
          />
        </Decorator>
      </PageSegment>
    </PageLayout>
  )
}

export default DigitalDelivery
